import { imageUrl } from '../config'
import { useWholeSaleCategoriesQuery } from '../generated/graphql'
import { useTranslation } from '../localization/useTranslation'
import { useAuthStore } from '../store/auth'

/**
 * A whole-sale category, flattened to what the "add by category" tiles need: the display name and
 * section resolved to the active locale (Core returns both a default and a `*Localized` list), the
 * hosted icon URL, and the flat `value` the potential is worth. `advanced` categories are the ones
 * v1 routes into product search; v2 already has photo + catalogue search, so we surface them as
 * plain flat-value tiles like the rest (kept on the model in case we want to treat them specially).
 */
export interface CategoryOption {
  id: string
  name: string
  section: string
  iconUrl?: string
  /** Flat trade-in value for the category, in shop currency (format via useMoneyFmt). */
  value: number
  advanced: boolean
}

/** One presentation-ordered section with its categories (sections preserve first-seen order). */
export interface CategorySection {
  section: string
  categories: CategoryOption[]
}

function pickLocalized(
  fallback: string | null | undefined,
  localized: ReadonlyArray<{ locale: string; value: string } | null> | null | undefined,
  locale: string,
): string {
  const hit = localized?.find((l) => l?.locale === locale)?.value
  return hit || fallback || ''
}

/**
 * Load the shop's whole-sale categories, resolved to the active locale and grouped into
 * presentation-ordered sections. `WholeSaleCategories` already comes back in PRESENTATION order, so
 * we keep sections in first-seen order and categories in their returned order within each section.
 * Cached (cache-first) — the list is small and shop-static.
 */
export function useCategoryOptions() {
  const shopId = useAuthStore((s) => s.shopId)
  const wholeSaleActive = useAuthStore((s) => s.config.wholeSaleActive)
  const { locale } = useTranslation()
  // Skip for shops that don't run categories at all. `wholeSaleActive` is only a policy flag, though
  // — it can be on with zero categories created — so callers must gate on `hasCategories`, not the
  // flag, to decide whether to offer the "add by category" path.
  const { data, loading, error } = useWholeSaleCategoriesQuery({
    variables: { shopId: shopId ?? '' },
    skip: !shopId || !wholeSaleActive,
    fetchPolicy: 'cache-first',
  })

  const options: CategoryOption[] = (data?.WholeSaleCategories ?? []).map((c) => ({
    id: c._id,
    name: pickLocalized(c.name, c.nameLocalized, locale),
    section: pickLocalized(c.section, c.sectionLocalized, locale),
    iconUrl: imageUrl(c.iconId),
    value: c.value,
    advanced: c.advanced ?? false,
  }))

  // Group into sections, preserving the order categories first appear (PRESENTATION order).
  const sections: CategorySection[] = []
  const bySection = new Map<string, CategorySection>()
  for (const opt of options) {
    let group = bySection.get(opt.section)
    if (!group) {
      group = { section: opt.section, categories: [] }
      bySection.set(opt.section, group)
      sections.push(group)
    }
    group.categories.push(opt)
  }

  return { options, sections, loading, error, hasCategories: options.length > 0 }
}
